import React, { useState, useEffect } from 'react';
import { 
  Eye, Calendar, Search, 
  RefreshCw, AlertCircle, FileText,
  Plus, ChevronLeft, ChevronRight,
  Edit
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../components/api';
import Layout from '../components/Layout';
import { toast } from 'react-toastify';

const ListeEmploisPage = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [selectedAnnee, setSelectedAnnee] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [emplois, setEmplois] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [anneesAcademiques, setAnneesAcademiques] = useState([]);
  const [erreur, setErreur] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setErreur(null);
    try {
      const response = await api.get('/emplois/', {
        params: {
          page: currentPage,
          page_size: pageSize,
          annee: selectedAnnee || ''
        }
      });

      if (response.data.status === 'success') {
        setEmplois(response.data.data);
        setTotalPages(response.data.pagination.total_pages);
        setTotalItems(response.data.pagination.total);
      } else {
        throw new Error(response.data.message || 'Erreur lors de la récupération des emplois du temps');
      }

      // Récupérer la liste des années académiques
      try {
        const anneesResponse = await api.get('/etablissement/annees-academiques/');
        setAnneesAcademiques(anneesResponse.data || []);
      } catch (anneesError) {
        console.warn('Impossible de récupérer les années académiques:', anneesError);
        setAnneesAcademiques([]);
      }
      
    } catch (error) {
      console.error('Erreur de chargement des données:', error);
      
      // Ne pas traiter les 404 comme des erreurs critiques
      if (error.response && error.response.status === 404) {
        setEmplois([]);
        setTotalPages(0);
        setTotalItems(0);
      } else {
        setErreur(error.message || 'Erreur de chargement des données');
        toast.error('Erreur de chargement des données');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, selectedAnnee]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleAnneeChange = (e) => {
    setSelectedAnnee(e.target.value);
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredEmplois = searchTerm.trim() 
    ? emplois.filter(emploi => 
        emploi.classe?.libelle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emploi.semestre?.libelle?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : emplois;

  const handleViewDetails = (emploi) => {
    navigate(`/details-emploi/${emploi.id}`);
  };

  const handleEdit = (emploi) => {
    navigate(`/edit-emploi/${emploi.id}`);
  };

  const Pagination = () => (
    <div className="flex items-center justify-between mt-6">
      <div className="flex-1 flex justify-between sm:hidden">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
            currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          Précédent
        </button>
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
            currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          Suivant
        </button>
      </div>
      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700">
            Affichage de{' '}
            <span className="font-medium">
              {((currentPage - 1) * pageSize) + 1}
            </span>{' '}
            à{' '}
            <span className="font-medium">
              {Math.min(currentPage * pageSize, totalItems)}
            </span>{' '}
            sur{' '}
            <span className="font-medium">{totalItems}</span> emplois du temps
          </p>
        </div>
        <div>
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              <span className="sr-only">Précédent</span>
              <ChevronLeft className="h-5 w-5" aria-hidden="true" />
            </button>
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i + 1}
                onClick={() => handlePageChange(i + 1)}
                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                  currentPage === i + 1
                    ? 'z-10 bg-[#202654] border-[#202654] text-white'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              <span className="sr-only">Suivant</span>
              <ChevronRight className="h-5 w-5" aria-hidden="true" />
            </button>
          </nav>
        </div>
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          {/* Card principale avec design professionnel */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {/* Header avec bannière bleue */}
            <div className="bg-gradient-to-r from-[#202654] to-[#202654]/90 px-6 py-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="flex items-center text-white">
                  <button
                    onClick={() => navigate(-1)}
                    className="mr-4 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 backdrop-blur-sm"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                  <div>
                    <h1 className="text-2xl font-bold">Gestion des Emplois du Temps</h1>
                    <p className="mt-1 text-blue-100">
                      Gérez et visualisez tous les emplois du temps des classes
                    </p>
                  </div>
                </div>
                <div className="mt-4 md:mt-0 flex flex-col sm:flex-row sm:space-x-3 space-y-3 sm:space-y-0">
                  <button
                    onClick={() => fetchData()}
                    className="inline-flex items-center justify-center px-4 py-2 border border-white/20 rounded-md shadow-sm text-sm font-medium text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-all duration-200"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Actualiser
                  </button>
                  <button
                    onClick={() => navigate('/create-emploi')}
                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-all duration-200"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Nouvel Emploi du Temps
                  </button>
                </div>
              </div>
            </div>

            {/* Filtres */}
            <div className="p-6 border-b border-gray-200 bg-gray-50">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Rechercher une classe, un semestre..."
                    value={searchTerm}
                    onChange={handleSearch}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm"
                  />
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="w-full sm:w-auto">
                    <select
                      value={selectedAnnee}
                      onChange={handleAnneeChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm"
                    >
                      <option value="">Toutes les années</option>
                      {anneesAcademiques.map(annee => (
                        <option key={annee.id} value={annee.id}>
                          {annee.libelle}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Contenu principal */}
            <div className="p-6">
              {erreur && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Erreur</h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>{erreur}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#202654]"></div>
                </div>
              ) : emplois.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Calendar className="h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-1">Aucun emploi du temps trouvé</h3>
                  <p className="text-gray-500 max-w-md mb-6">
                    {selectedAnnee 
                      ? "Aucun emploi du temps n'a été créé pour l'année académique sélectionnée." 
                      : "Aucun emploi du temps n'a été créé dans le système. Commencez par créer un nouvel emploi du temps."}
                  </p>
                  <button
                    onClick={() => navigate('/create-emploi')}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#202654] hover:bg-[#202654]/90"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Créer un emploi du temps
                  </button>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Classe</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Semestre</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Année Académique</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre de Cours</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredEmplois.map((emploi) => (
                          <tr key={emploi.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-[#202654]/10 flex items-center justify-center">
                                  <Calendar className="h-4 w-4 text-[#202654]" />
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">{emploi.classe?.libelle}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{emploi.semestre?.libelle}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{emploi.annee?.libelle}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{emploi.cours?.length || 0} cours</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleViewDetails(emploi)}
                                  className="text-indigo-600 hover:text-indigo-900"
                                  title="Voir les détails"
                                >
                                  <Eye size={18} />
                                </button>
                                <button
                                  onClick={() => handleEdit(emploi)}
                                  className="text-blue-600 hover:text-blue-900"
                                  title="Modifier"
                                >
                                  <Edit size={18} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && <Pagination />}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ListeEmploisPage;
